import React, { useCallback, useMemo, useState } from 'react'
import { MacroType, TemplateKeywordsField, Well } from '@pushly/aqe/lib/components'
import type { CustomTagProps } from '@rc-component/select/lib/BaseSelect'
import { GlobalOutlined, InfoCircleOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons'
import { IconMacro } from '../../svg/icon-macro'
import { containsMacro } from '../../../_utils/macro-validation'
import { MediaFieldSource, MediaFieldSourceKind } from './template-fields/media-field-sources'
import TemplateTitleField from './template-fields/title-field'
import TemplateBodyField from './template-fields/body-field'
import TemplateLandingUrlField from './template-fields/landing-url-field'
import LandingUrlActionButton from './template-fields/landing-url-action-button'
import { NotificationContentModel } from '../../../models/notification/notification-content.model'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import TemplateMediaField from './template-fields/media-field'
import { PromiseableProp } from '../../../_utils/promiseable-prop'
import { getClassNames } from '../../../_utils/classnames'
import { DomainDto } from '../../../dtos/domain'
import NotificationExperienceOptions from './notification-experience-options'
import CharacterLimitTooltip from './character-limit-tooltip'
import { NotificationBuilderLevel, NotificationBuilderTheme } from '../enums'
import { useService } from '../../../hooks/use-service'
import { AppState } from '../../../stores/app'
import { NotificationChannelSettings } from './notification-channel-settings'
import { NotificationNativeAndroidContentModel } from '../../../models/notification/notification-native-android-content.model'
import { NotificationNativeIosContentModel } from '../../../models/notification/notification-native-ios-content.model'
import { NotificationDefaultContentModel } from '../../../models/notification/notification-default-content.model'
import { NotificationWebContentModel } from '../../../models/notification/notification-web-content.model'
import { CatalogItemType, DeliveryChannel } from '@pushly/models/lib/enums'
import { RecommendationSource } from '@pushly/models/lib/enums/recommendations/recommendation-source'
import {
    INotificationBuilderState,
    NotificationBuilderMacroCategoryMap,
} from '../interfaces/notification-builder-state.interface'
import { ActionDispatcher } from '../types'
import { MacroOptionType } from '@pushly/aqe/lib/components/macro-manager/macro-manager'
import { Divider, Flex, Popover, Skeleton, Switch, Tag, Typography } from 'antd'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { FieldSuggestionResultsManager } from './field-suggestion-results-manager/'
import { formatFieldNamesContent } from './field-suggestion-results-manager/format-field-names'
import { useSharedChannelFieldSetter } from '../hooks/use-shared-channel-field-setter'
import { useFieldSuggestionResultsManager } from '../hooks/use-field-suggestion-results-setter'
import { PageAnalysisTarget } from '../hooks/use-page-analyzer'
import { NotificationActionsBuilder } from './notification-actions-builder'
import { RemoteErrorMessageDisplay } from './field-suggestion-results-manager/components/remote-error-message-display'
import { FEAT_ENABLE_NOTIFICATION_DISPLAY_BEHAVIOR } from '../../../constants'

const MACRO_DOCS_URL = 'https://documentation.pushly.com/platform/notifications/macros'

interface INotificationContentBuilder {
    className?: string
    builder: INotificationBuilderState
    level: NotificationBuilderLevel
    theme: NotificationBuilderTheme
    domain: DomainDto
    /** The domain Page Analysis runs under when it is not `domain` (an org send). */
    pageAnalysisDomain?: DomainDto
    loading?: boolean
    title?: string
    value: NotificationContentModel
    channels: DeliveryChannel[]
    dispatchChanges: ActionDispatcher
    onChange: (value: NotificationContentModel) => void
    /**
     * Clears all fields and resets content to default values.
     * @note Not currently implemented
     */
    onClear: () => void
    disabled?: boolean
    experienceDisabled?: boolean
    keywordOptions?: PromiseableProp<any>
    customMacroOptions?: PromiseableProp<any>
    secretsMacroOptions?: PromiseableProp<any>
    macroCategories?: { urlFields?: MacroType[]; messageFields?: MacroType[] }
    hideTtlSettings?: boolean
    hidePageAnalysisToggle?: boolean
    hidePushlyAiToggle?: boolean
}

type ContentUpdate =
    | NotificationDefaultContentModel
    | NotificationWebContentModel
    | NotificationNativeIosContentModel
    | NotificationNativeAndroidContentModel

// Duration for field suggestion success notifications (in seconds)
const FIELD_SUGGESTION_SUCCESS_DURATION = 8
const FIELD_SUGGESTION_SUCCESS_NOTIFICATION_KEY = 'notification_content_suggestion_success'

const KEYWORD_TRUNCATION_THRESHOLD = 45

const SkeletonWrapper = (props: { loading; children }) => (
    <Skeleton loading={props.loading} active={true} avatar={false} title={false}>
        {props.children}
    </Skeleton>
)

/** Fallback defaults when a consumer doesn't configure macro groups. */
export const DEFAULT_URL_FIELD_MACRO_TYPES: MacroType[] = [
    'domain',
    'device',
    'location',
    'notification',
    'secrets',
    'custom',
]
export const DEFAULT_MESSAGE_FIELD_MACRO_TYPES: MacroType[] = ['location', 'secrets', 'custom']

/**
 * Which macro groups each field offers.
 *
 * Consumers configure these via `builderProps.macroProps.categories`, so the
 * builder is the source that matters — no live render path supplies the
 * `macroCategories` prop. An explicit prop still wins, for a direct caller.
 */
export function resolveMacroCategories(
    explicit: NotificationBuilderMacroCategoryMap | undefined,
    fromBuilder: NotificationBuilderMacroCategoryMap | undefined,
): { urlFields: MacroType[]; messageFields: MacroType[] } {
    const resolved = explicit ?? fromBuilder
    return {
        urlFields: resolved?.urlFields ?? DEFAULT_URL_FIELD_MACRO_TYPES,
        messageFields: resolved?.messageFields ?? DEFAULT_MESSAGE_FIELD_MACRO_TYPES,
    }
}

/**
 * Scope tokens for context-specific macros. Each token belongs to its own
 * vocabulary (catalog item type vs. recommendation source) and getMacroOptions
 * matches each vocabulary independently, so contributing one never suppresses
 * macros scoped by the other.
 */
export function resolveMacroScopeTokens(
    itemType: CatalogItemType | undefined,
    recommendationSource: RecommendationSource | undefined,
): (MacroOptionType | RecommendationSource)[] {
    // Two independent vocabularies: catalog item type (a MacroOptionType /
    // CatalogItemType) and recommendation source. aqe's macro filter matches each
    // option's `option_types` against these tokens, and recommendation-source
    // tokens are intentionally passed to scope source-specific article macros.
    // aqe types `MacroOptionType` as CatalogItemType only, so RecommendationSource
    // is not a subtype — the honest return type is the union of both. The single
    // cast to MacroOptionType[] happens at the aqe prop boundary below.
    return [...(itemType ? [itemType] : []), ...(recommendationSource ? [recommendationSource] : [])]
}

const NotificationContentBuilder = (props: INotificationContentBuilder) => {
    const {
        className,
        builder,
        dispatchChanges,
        level,
        domain,
        loading,
        value,
        channels = [],
        onChange,
        macroCategories,
        hideTtlSettings,
        hidePageAnalysisToggle = false,
        hidePushlyAiToggle = false,
        disabled,
        experienceDisabled,
    } = props

    const appState = useService(AppState)

    const [usingAltNativeUrl, setUsingAltNativeUrl] = useState<boolean>(!!value.getDefaultContent().getNativeAltUrl())
    const [localNativeUrl, setLocalNativeUrl] = useState<string | null | undefined>(
        value.getDefaultContent().getNativeAltUrl(),
    )

    const isOrgLevel = level === NotificationBuilderLevel.ORG
    const isDomainLevel = level === NotificationBuilderLevel.DOMAIN
    const isJourneyLevel = level === NotificationBuilderLevel.CAMPAIGN
    const levelClassName = isJourneyLevel ? 'journey' : isOrgLevel ? 'org' : 'domain'
    const isMobile = props.theme === NotificationBuilderTheme.MOBILE

    // The options for macro groups whose contents belong to this domain, read off the builder's own
    // config where the caller did not hand them in: a caller that never renders these fields itself
    // — a journey's send step, mounting the whole builder — has to state them through the config.
    const dynamicMacroOptions = builder.macroProps?.dynamicOptions

    const { urlFields: landingUrlMacroTypes, messageFields: messageFieldMacroTypes } = resolveMacroCategories(
        macroCategories,
        builder.macroProps?.categories,
    )

    const workingModel = value.getDefaultContent().clone()

    // TODO: consider reworking to handle per channel updates more field specifically
    const handleChange = (update: ContentUpdate, channel?: DeliveryChannel) => {
        const change = value.clone()

        switch (channel) {
            case DeliveryChannel.WEB:
                change.setWebContent(update as NotificationWebContentModel)
                break
            case DeliveryChannel.NATIVE_IOS:
                change.setNativeIosContent(update as NotificationNativeIosContentModel)
                break
            case DeliveryChannel.NATIVE_ANDROID:
                change.setNativeAndroidContent(update as NotificationNativeAndroidContentModel)
                break
            default:
                change.setDefaultContent(update as NotificationDefaultContentModel)
        }

        onChange?.(change)
    }

    const setSharedChannelField = useSharedChannelFieldSetter(value, channels, onChange)

    const nativeChannelSelected =
        channels.includes(DeliveryChannel.NATIVE_IOS) || channels.includes(DeliveryChannel.NATIVE_ANDROID)
    const showIOSFields =
        (isOrgLevel || isDomainLevel || isJourneyLevel) && channels.includes(DeliveryChannel.NATIVE_IOS)
    const showUseDifferentUrlForNative = channels.includes(DeliveryChannel.WEB) && nativeChannelSelected

    // aqe's `validMacroOptionTypes` prop is typed `MacroOptionType[]` (== CatalogItemType[]),
    // but we also declare recommendation-source scope tokens here. Those tokens are inert in
    // aqe's current filter (article_recommendation macros carry no `option_types`) yet are the
    // documented mechanism for scoping source-specific macros, so we assert to the prop's type
    // at this single boundary rather than widening aqe's `MacroOptionType`.
    const validMacroOptionTypes = resolveMacroScopeTokens(
        domain.ecommConfig?.itemType,
        builder.macroProps?.recommendationSource,
    ) as MacroOptionType[]

    // --- Icon / Image source configuration ---
    // The image "item macro" source (tag + label) is supplied entirely by the consumer via
    // `macroProps.imageMacroProps`; the field offers it only when that config is present. Nothing
    // image-type/ecomm-specific is assumed here — the consumer owns what the macro is and how it's labeled.
    const imageMacroProps = builder.macroProps?.imageMacroProps
    const imageMacroTag = imageMacroProps?.tag
    const imageMacroTagLabel = imageMacroProps?.label

    // Dynamic-macro icons are supported everywhere except org-level templates (which fan out across
    // domains). Domain Default + None are available at every level.
    const iconSupportsDynamicMacro = !isOrgLevel
    const iconSources: MediaFieldSource[] = [
        {
            key: 'domain-default',
            label: 'Default',
            icon: <GlobalOutlined aria-hidden="true" />,
            tooltip: isOrgLevel
                ? 'Uses each domain’s configured default icon when sent. Managed in Domain settings.'
                : 'Uses the domain’s default icon. Managed in Domain settings.',
        },
        { key: 'upload', label: 'Custom', icon: <PlusOutlined aria-hidden="true" />, tooltip: 'Upload a custom icon.' },
        ...(iconSupportsDynamicMacro
            ? [
                  {
                      key: 'dynamic-macro',
                      label: 'Macro',
                      icon: <IconMacro />,
                      tooltip: 'Use a per-subscriber macro or URL, resolved at send time.',
                  } satisfies MediaFieldSource,
              ]
            : []),
        {
            key: 'none',
            label: 'None',
            icon: <StopOutlined aria-hidden="true" />,
            tooltip: 'No icon will be shown on this notification.',
        },
    ]

    const deriveIconSource = (): MediaFieldSourceKind => {
        if (workingModel.getIsUsingDomainDefaultIcon()) return 'domain-default'
        const url = workingModel.getIconUrl()
        if (iconSupportsDynamicMacro && containsMacro(url)) return 'dynamic-macro'
        // A new notification often carries icon_url = the domain default with the flag unset; treat that
        // as Domain Default (not a custom Upload) so the default selection is correct and Upload starts empty.
        if (url && domain?.defaultIconUrl && url === domain.defaultIconUrl) return 'domain-default'
        if (url) return 'upload'
        return 'none'
    }

    // The fixed "item image" source appears only when the consumer supplied its macro config.
    const imageSupportsItemMacro = !!imageMacroProps
    const wrappedItemImageMacro = imageMacroTag ? `{{${imageMacroTag}}}` : undefined
    const imageSources: MediaFieldSource[] = [
        {
            key: 'upload',
            label: 'Custom',
            icon: <PlusOutlined aria-hidden="true" />,
            tooltip: 'Upload a custom image.',
        },
        ...(imageSupportsItemMacro
            ? [
                  {
                      key: 'fixed-macro',
                      label: imageMacroTagLabel,
                      icon: <IconMacro />,
                      tooltip: 'Use the catalog item’s image for each notification.',
                  } satisfies MediaFieldSource,
              ]
            : []),
        {
            key: 'dynamic-macro',
            label: 'Macro',
            icon: <IconMacro />,
            tooltip: 'Use a per-subscriber macro or URL (e.g. remote_fetch), resolved at send time.',
        },
        {
            key: 'none',
            label: 'None',
            icon: <StopOutlined aria-hidden="true" />,
            tooltip: 'No image will be shown on this notification.',
        },
    ]

    const deriveImageSource = (): MediaFieldSourceKind => {
        const url = workingModel.getImageUrl()
        // The fixed item-image macro is one specific token; any other `{{…}}`/`{%…%}` is a free dynamic macro.
        if (imageSupportsItemMacro && url?.trim() === wrappedItemImageMacro) return 'fixed-macro'
        if (containsMacro(url)) return 'dynamic-macro'
        if (url) return 'upload'
        // A new notification defaults to Custom (ready to upload an image); an existing notification
        // saved without an image reflects that intentional choice as None.
        return builder.mode === 'create' ? 'upload' : 'none'
    }

    /**
     * Extract the landing URL according to the notification channels targeted.
     * Default should be used - if missing web is priority followed by native if available.
     */
    const getTargetedLandingUrl = (content: NotificationContentModel) => {
        const enabledChannels = builder.channels
        const defaultContent = content.getDefaultContent()

        if (defaultContent.getLandingUrl()) {
            return defaultContent.getLandingUrl()
        } else {
            if (enabledChannels.includes(DeliveryChannel.WEB)) {
                return content.getWebContent().getLandingUrl()
            } else {
                return defaultContent.getNativeAltUrl()
            }
        }
    }

    const {
        localFieldSuggestionResults,
        setLocalFieldSuggestionResults,
        applyFieldSuggestionResults,
        suggestedImageComposite,
        setSuggestedImageComposite,
    } = useFieldSuggestionResultsManager(value, channels, onChange)

    const keywordTagRender = useCallback((tagProps: CustomTagProps) => {
        const { label, value: tagValue, closable, onClose } = tagProps
        const labelStr = String(label ?? tagValue ?? '')

        if (labelStr.length > KEYWORD_TRUNCATION_THRESHOLD) {
            const truncated = labelStr.slice(0, KEYWORD_TRUNCATION_THRESHOLD) + '…'
            return (
                <Popover content={labelStr} overlayStyle={{ maxWidth: 400, wordBreak: 'break-all' }}>
                    <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                        {truncated}
                    </Tag>
                </Popover>
            )
        }

        return (
            <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                {labelStr}
            </Tag>
        )
    }, [])

    // An org send has no domain of its own, so Page Analysis runs under the first selected domain it
    // can fill anything on, and stores the suggested image under the org, as the org image field does.
    const pageAnalysisDomain = props.pageAnalysisDomain ?? domain
    const pageAnalysisTarget = useMemo<PageAnalysisTarget>(
        () => ({
            domain: pageAnalysisDomain,
            mediaOwner: isOrgLevel ? { type: 'account', id: pageAnalysisDomain.accountId } : undefined,
        }),
        [pageAnalysisDomain, isOrgLevel],
    )

    const getPageAnalysisResults = useCallback(
        async (pageAnalysisURL: string, mode: 'auto' | 'advanced' = 'auto') => {
            try {
                const { suggestions: pageAnalysisResults, error: analysisError } = await builder.pageAnalyzer.analyze(
                    pageAnalysisURL,
                    pageAnalysisTarget,
                )

                if (analysisError) {
                    if (mode === 'advanced') {
                        setLocalFieldSuggestionResults({
                            source: 'page_analyzer',
                            data: [],
                            error: analysisError,
                        })
                        return
                    }

                    simpleNotification('warning', <RemoteErrorMessageDisplay error={analysisError} />)
                    return
                }

                if (!pageAnalysisResults) {
                    simpleNotification('info', 'We were unable to find any suggestions for the provided Landing URL.')
                    return
                }

                const resultFieldNames = Object.entries(pageAnalysisResults)
                    .filter(([k, v]) => {
                        // Only include actual notification fields
                        const notificationFields = ['title', 'body', 'imageUrl', 'keywords']
                        return notificationFields.includes(k) && (Array.isArray(v) ? !!v.length : !!v)
                    })
                    .map(([k]) => titleCase(k).replace(/\surl$/i, ''))

                const resultFieldNamesContent = formatFieldNamesContent(resultFieldNames)

                if (mode === 'advanced') {
                    setLocalFieldSuggestionResults({ source: 'page_analyzer', data: [pageAnalysisResults] })
                    return
                }

                simpleNotification(
                    'success',
                    <span>
                        We've update the notification {resultFieldNamesContent} with suggestions from the provided
                        Landing URL
                        {isOrgLevel ? ` using ${pageAnalysisDomain.displayName}'s Page Analysis settings` : ''}.
                    </span>,
                    FIELD_SUGGESTION_SUCCESS_DURATION,
                    FIELD_SUGGESTION_SUCCESS_NOTIFICATION_KEY,
                )

                applyFieldSuggestionResults(pageAnalysisResults, { source: 'page_analyzer', url: pageAnalysisURL })
            } catch (pageAnalysisError) {
                if (pageAnalysisError) {
                    console.warn(pageAnalysisError)
                }
            }
        },
        [builder.pageAnalyzer, pageAnalysisTarget, applyFieldSuggestionResults, setLocalFieldSuggestionResults],
    )

    const getPushlyAiResults = useCallback(
        async (_sourceLandingUrl: string, mode: 'auto' | 'advanced' = 'auto') => {
            try {
                if (mode === 'advanced') {
                    const { allSuggestions: pushlyAiAdvancedResults, error: advancedModeAiError } =
                        await builder.pushlyAi.generateNotificationVariants(
                            builder.variants[builder.selectedVariantIdx],
                        )

                    if (advancedModeAiError) {
                        setLocalFieldSuggestionResults({
                            source: 'pushly_ai',
                            data: [],
                            error: advancedModeAiError,
                        })
                        return
                    }

                    if (!pushlyAiAdvancedResults || pushlyAiAdvancedResults.length === 0) {
                        simpleNotification(
                            'info',
                            'We were unable to generate any suggestions for the provided Landing URL.',
                        )
                        return
                    }

                    setLocalFieldSuggestionResults({ source: 'pushly_ai', data: pushlyAiAdvancedResults })
                    return
                }

                // Auto mode - use the original method that returns one variant
                const { suggestions: pushlyAiResults, error: aiError } =
                    await builder.pushlyAi.generateNotificationVariant(builder.variants[builder.selectedVariantIdx])

                if (aiError) {
                    simpleNotification('warning', <RemoteErrorMessageDisplay error={aiError} />)
                    return
                }

                if (!pushlyAiResults) {
                    simpleNotification(
                        'info',
                        'We were unable to generate any suggestions for the provided Landing URL.',
                    )
                    return
                }

                const resultFieldNames = Object.entries(pushlyAiResults)
                    .filter(([k, v]) => {
                        // Only include actual notification fields
                        const notificationFields = ['title', 'body', 'imageUrl', 'keywords']
                        return notificationFields.includes(k) && (Array.isArray(v) ? !!v.length : !!v)
                    })
                    .map(([k]) => titleCase(k).replace(/\surl$/i, ''))

                const resultFieldNamesContent = formatFieldNamesContent(resultFieldNames)

                simpleNotification(
                    'success',
                    <span>
                        We've update the notification {resultFieldNamesContent} with generated suggestions from the
                        provided Landing URL.
                    </span>,
                    FIELD_SUGGESTION_SUCCESS_DURATION,
                    FIELD_SUGGESTION_SUCCESS_NOTIFICATION_KEY,
                )

                applyFieldSuggestionResults(pushlyAiResults, {
                    source: 'pushly_ai',
                    generationId: builder.pushlyAi.generationIdRef.current,
                    variantIndex: builder.pushlyAi.lastServedVariantIndexRef.current ?? undefined,
                })
            } catch (pageAnalysisError) {
                if (pageAnalysisError) {
                    console.warn(pageAnalysisError)
                }
            }
        },
        [
            builder.pushlyAi,
            applyFieldSuggestionResults,
            setLocalFieldSuggestionResults,
            builder.variants,
            builder.selectedVariantIdx,
        ],
    )

    return (
        <>
            <Well
                className={getClassNames('notification-interactions-builder', levelClassName, className)}
                title="Destination"
                mode="ghost"
                footerMode="hidden"
                actions={
                    loading ? undefined : (
                        <div className="content-builder-actions">
                            <div className="message-info-toggle">
                                <CharacterLimitTooltip className={getClassNames('notification-message-info-overlay')}>
                                    <span role="button" onClick={() => {}} style={{ cursor: 'pointer' }}>
                                        <span>Character Limits</span>
                                        <InfoCircleOutlined className="info-icon" />
                                    </span>
                                </CharacterLimitTooltip>
                            </div>
                        </div>
                    )
                }
            >
                <SkeletonWrapper loading={loading}>
                    <TemplateLandingUrlField
                        expandable={!isMobile}
                        className={getClassNames(null, 'landing-url-field', {
                            'show-page-analysis-toggle': !hidePageAnalysisToggle,
                            'show-pushly-ai-toggle': !hidePushlyAiToggle,
                        })}
                        disabled={disabled}
                        value={getTargetedLandingUrl(value)}
                        onChange={(v) => {
                            workingModel.setLandingUrl(v)
                            // when alt native url is not set, the landing url is shared across channels
                            if (!usingAltNativeUrl) {
                                setSharedChannelField('LandingUrl', v)
                            } else {
                                // restrict change to default / web
                                handleChange(workingModel)
                            }
                        }}
                        macroTypes={landingUrlMacroTypes}
                        customMacroOptions={props.customMacroOptions}
                        secretsMacroOptions={props.secretsMacroOptions}
                        dynamicMacroOptions={dynamicMacroOptions}
                        validMacroOptionTypes={validMacroOptionTypes}
                        formItemProps={{ layout: 'vertical' }}
                        addon={
                            <LandingUrlActionButton
                                domain={pageAnalysisDomain}
                                settingsDomainName={isOrgLevel ? pageAnalysisDomain.displayName : undefined}
                                value={getTargetedLandingUrl(value)}
                                disabled={disabled}
                                hidePushlyAiToggle={hidePushlyAiToggle}
                                onPushlyAiClick={getPushlyAiResults}
                                hidePageAnalysisToggle={hidePageAnalysisToggle}
                                onPageAnalysisClick={getPageAnalysisResults}
                                segmentsLoaded={
                                    builder.availableSegmentsLoaded &&
                                    !!builder.variants[builder.selectedVariantIdx]?.getAudience()?.getSegmentIds()
                                        ?.length
                                }
                            />
                        }
                    />

                    {showUseDifferentUrlForNative && (
                        <TemplateLandingUrlField
                            expandable={!isMobile}
                            disabled={!usingAltNativeUrl || disabled}
                            value={workingModel?.getNativeAltUrl()}
                            onChange={(v) => {
                                if (v) {
                                    setLocalNativeUrl(v)
                                    workingModel.setNativeAltUrl(v)
                                    setSharedChannelField('LandingUrl', v, {
                                        updateChannels: [DeliveryChannel.NATIVE_ANDROID, DeliveryChannel.NATIVE_IOS],
                                    })
                                }
                            }}
                            label={
                                <span className="native-alt-url">
                                    <Switch
                                        size="small"
                                        checked={usingAltNativeUrl}
                                        aria-label="Use a Different URL for Native App"
                                        onChange={(v) => {
                                            setUsingAltNativeUrl(v)
                                            if (v) {
                                                workingModel?.setNativeAltUrl(
                                                    workingModel.getNativeAltUrl() ?? localNativeUrl,
                                                )
                                                setSharedChannelField(
                                                    'LandingUrl',
                                                    workingModel.getNativeAltUrl() ?? localNativeUrl,
                                                    {
                                                        updateChannels: [
                                                            DeliveryChannel.NATIVE_ANDROID,
                                                            DeliveryChannel.NATIVE_IOS,
                                                        ],
                                                    },
                                                )
                                            } else {
                                                // when removing alternate url set any native channels back to the shared landing url field value
                                                workingModel?.setNativeAltUrl(undefined)
                                                setSharedChannelField('LandingUrl', workingModel.getLandingUrl(), {
                                                    updateChannels: [
                                                        DeliveryChannel.NATIVE_ANDROID,
                                                        DeliveryChannel.NATIVE_IOS,
                                                    ],
                                                })
                                            }
                                        }}
                                    />
                                    <span>Use a Different URL for Native App</span>
                                </span>
                            }
                            placeholder="myapp://story/12345"
                            macroTypes={landingUrlMacroTypes}
                            customMacroOptions={props.customMacroOptions}
                            secretsMacroOptions={props.secretsMacroOptions}
                            dynamicMacroOptions={dynamicMacroOptions}
                            validMacroOptionTypes={validMacroOptionTypes}
                            formItemProps={{ layout: 'vertical' }}
                        />
                    )}
                </SkeletonWrapper>
            </Well>

            <Divider size="small" />

            <Well
                className={getClassNames('notification-design-builder', levelClassName, className)}
                title="Message"
                variant="ghost"
                footerMode="hidden"
            >
                <SkeletonWrapper loading={loading}>
                    <TemplateTitleField
                        placeholder="Notification title"
                        disabled={disabled}
                        expandable={!isMobile}
                        value={workingModel.getTitle()}
                        onChange={(v) => {
                            workingModel.setTitle(v)
                            setSharedChannelField('Title', v)
                        }}
                        macroTypes={messageFieldMacroTypes}
                        customMacroOptions={props.customMacroOptions}
                        secretsMacroOptions={props.secretsMacroOptions}
                        dynamicMacroOptions={dynamicMacroOptions}
                        validMacroOptionTypes={validMacroOptionTypes}
                        formItemProps={{ layout: 'vertical' }}
                    />
                    <TemplateBodyField
                        placeholder="Notification body"
                        disabled={disabled}
                        expandable={!isMobile}
                        value={workingModel.getBody()}
                        onChange={(v) => {
                            workingModel.setBody(v)
                            setSharedChannelField('Body', v || null)
                        }}
                        macroTypes={messageFieldMacroTypes}
                        customMacroOptions={props.customMacroOptions}
                        secretsMacroOptions={props.secretsMacroOptions}
                        dynamicMacroOptions={dynamicMacroOptions}
                        validMacroOptionTypes={validMacroOptionTypes}
                        formItemProps={{ layout: 'vertical' }}
                    />

                    {/* Wrap so the icon field drops below the image field instead of overflowing when
                        the container (e.g. a drawer) is too narrow for both side by side. */}
                    <Flex gap={16} wrap="wrap">
                        <TemplateMediaField
                            mobileFriendly={props.theme === NotificationBuilderTheme.MOBILE}
                            disabled={disabled}
                            label={
                                <span>
                                    <span className="label-with-component-r">Image:</span>
                                    <Popover
                                        classNames={{
                                            root: getClassNames('popover'),
                                            content: getClassNames('notification-builder-popover', 'image-popover'),
                                        }}
                                        content={
                                            <>
                                                <p>
                                                    This image will be displayed underneath the title and body of the
                                                    notification on Windows and Android. Recommended minimum size is
                                                    360x180 with an aspect ratio of 2:1.
                                                </p>
                                                <p>
                                                    <strong>Upload</strong> a static image, use a <strong>Macro</strong>{' '}
                                                    for a dynamic, per-subscriber image, or <strong>None</strong> to
                                                    send no image.
                                                </p>
                                                {imageSupportsItemMacro && (
                                                    <p>
                                                        <strong>{imageMacroTagLabel}</strong> uses the catalog item’s
                                                        image for each notification.
                                                    </p>
                                                )}
                                                <p>
                                                    A dynamic value can be a Liquid macro, a URL with an embedded macro,
                                                    or a <code>remote_fetch</code> tag.{' '}
                                                    <a href={MACRO_DOCS_URL} target="_blank" rel="noopener noreferrer">
                                                        Learn about macros
                                                    </a>
                                                    .
                                                </p>
                                            </>
                                        }
                                    >
                                        <InfoCircleOutlined className="info-icon" />
                                    </Popover>
                                </span>
                            }
                            className="image-upload"
                            domainId={domain.id}
                            defaultDomainMediaUrl={isOrgLevel ? undefined : domain?.defaultIconUrl}
                            accountId={isOrgLevel ? domain.accountId : undefined}
                            defaultCompositeValue={
                                suggestedImageComposite && {
                                    url: suggestedImageComposite.url,
                                    filename: suggestedImageComposite.filename,
                                    providerOptions: {
                                        originalMediaUrl: suggestedImageComposite.originalMediaUrl,
                                        localMediaSource: suggestedImageComposite.src,
                                        imageState: suggestedImageComposite.imageState,
                                    },
                                }
                            }
                            key={`image-${builder.selectedVariantIdx}`}
                            value={workingModel.getImageUrl()}
                            cropRatio={2}
                            imageType="notification-image"
                            pjpg={true}
                            forceCrop={true}
                            size="large"
                            sources={isMobile ? undefined : imageSources}
                            initialSource={isMobile ? undefined : deriveImageSource()}
                            macroTag={imageMacroTag}
                            macroIcon={<IconMacro />}
                            macroPicker={{
                                fieldName: 'Image macro',
                                macroTypes: landingUrlMacroTypes,
                                customMacroOptions: props.customMacroOptions,
                                secretsMacroOptions: props.secretsMacroOptions,
                                validMacroOptionTypes,
                            }}
                            onChange={
                                isMobile
                                    ? (v) => {
                                          setSuggestedImageComposite(undefined)
                                          setSharedChannelField('ImageUrl', v ?? null)
                                      }
                                    : undefined
                            }
                            onSourceValueChange={
                                isMobile
                                    ? undefined
                                    : ({ value }) => {
                                          setSuggestedImageComposite(undefined)
                                          setSharedChannelField('ImageUrl', value)
                                      }
                            }
                        />

                        <TemplateMediaField
                            key={`icon-${builder.selectedVariantIdx}`}
                            mobileFriendly={props.theme === NotificationBuilderTheme.MOBILE}
                            disabled={disabled}
                            label={
                                <span>
                                    <span className="label-with-component-r">Icon:</span>
                                    <Popover
                                        classNames={{
                                            root: getClassNames('popover'),
                                            content: getClassNames('notification-builder-popover', 'icon-popover'),
                                        }}
                                        content={
                                            <>
                                                <p>
                                                    This image will be displayed as a medium-size icon next to the title
                                                    and body of the notification on all platforms. Recommended size is
                                                    512x512 and an aspect ratio of 1:1.
                                                </p>

                                                <p>
                                                    <strong>Default</strong> uses{' '}
                                                    {isOrgLevel ? 'each domain’s' : 'the domain’s'} configured default
                                                    icon (managed in Domain settings). <strong>Upload</strong> a static
                                                    icon, use a <strong>Macro</strong> for a dynamic, per-subscriber
                                                    icon, or <strong>None</strong> to send no icon.
                                                </p>

                                                <p>
                                                    A dynamic value can be a Liquid macro, a URL with an embedded macro,
                                                    or a <code>remote_fetch</code> tag.{' '}
                                                    <a href={MACRO_DOCS_URL} target="_blank" rel="noopener noreferrer">
                                                        Learn about macros
                                                    </a>
                                                    .
                                                </p>

                                                <p>
                                                    <strong>Note:</strong> Changing this icon has no effect for Safari
                                                    notifications. Instead, the subscriber will receive a notification
                                                    with the default icon that was set on your domain when they
                                                    subscribed.
                                                </p>
                                            </>
                                        }
                                    >
                                        <InfoCircleOutlined className="info-icon" />
                                    </Popover>
                                </span>
                            }
                            className="icon-upload"
                            domainId={domain.id}
                            accountId={isOrgLevel ? domain.accountId : undefined}
                            value={workingModel.getIconUrl()}
                            cropRatio={1}
                            cropDim={[512, 512]}
                            imageType="notification-icon"
                            forceCrop={true}
                            sources={isMobile ? undefined : iconSources}
                            initialSource={isMobile ? undefined : deriveIconSource()}
                            domainDefaultPreviewUrl={isOrgLevel ? undefined : domain?.defaultIconUrl}
                            macroPicker={{
                                fieldName: 'Icon macro',
                                macroTypes: landingUrlMacroTypes,
                                customMacroOptions: props.customMacroOptions,
                                secretsMacroOptions: props.secretsMacroOptions,
                                validMacroOptionTypes,
                            }}
                            onChange={
                                isMobile
                                    ? (v) => {
                                          const result = setSharedChannelField('IconUrl', v ?? null, {
                                              waitToCompleteChange: true,
                                          })
                                          result.getDefaultContent().setIsUsingDomainDefaultIcon(false)
                                          onChange?.(result)
                                      }
                                    : undefined
                            }
                            onSourceValueChange={
                                isMobile
                                    ? undefined
                                    : ({ source, value }) => {
                                          const result = setSharedChannelField('IconUrl', value, {
                                              waitToCompleteChange: true,
                                          })
                                          result
                                              .getDefaultContent()
                                              .setIsUsingDomainDefaultIcon(source === 'domain-default')
                                          onChange?.(result)
                                      }
                            }
                        />
                    </Flex>
                </SkeletonWrapper>
            </Well>

            <Divider size="small" />

            <Well
                className={getClassNames(
                    'notification-interactions-builder',
                    'buttons-builder thin',
                    levelClassName,
                    className,
                    {
                        collapsed: !workingModel.getCustomActionsEnabled(),
                    },
                )}
                title={
                    <>
                        <Switch
                            size="small"
                            disabled={disabled}
                            checked={workingModel.getCustomActionsEnabled()}
                            aria-label="Customize Buttons"
                            onChange={(enabled) => {
                                const update = workingModel.clone()

                                update.setCustomActionsEnabled(enabled)
                                if (!enabled) {
                                    update.setActions(undefined)
                                }

                                handleChange(update)
                            }}
                        />

                        <span className="title">
                            <span className="label-with-component-r">Customize Buttons</span>
                            <Popover
                                classNames={{
                                    root: getClassNames('popover'),
                                    content: getClassNames('notification-builder-popover', 'buttons-popover'),
                                }}
                                content={
                                    <div>
                                        <Typography.Paragraph>
                                            By default, all notifications have a “Visit Site” button that, when clicked,
                                            directs the user to the primary landing URL. Enabling this setting allows
                                            you to customize the button behavior, label, & landing URL and add an
                                            optional 2nd button.
                                        </Typography.Paragraph>
                                        <Typography.Paragraph>
                                            Learn more about{' '}
                                            <a
                                                href={`${appState.documentationLink}/platform/notifications/action-buttons`}
                                                target="_blank"
                                            >
                                                Custom Buttons
                                            </a>
                                            .
                                        </Typography.Paragraph>
                                    </div>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </span>
                    </>
                }
                variant="ghost"
                footerMode="hidden"
                collapsed={!workingModel.getCustomActionsEnabled()}
            >
                <SkeletonWrapper loading={loading}>
                    {workingModel.getCustomActionsEnabled() && (
                        <NotificationActionsBuilder
                            loading={loading}
                            domain={isOrgLevel ? null : domain}
                            customMacroOptions={props.customMacroOptions}
                            secretsMacroOptions={props.secretsMacroOptions}
                            validMacroOptionTypes={validMacroOptionTypes}
                            value={workingModel}
                            onChange={(v) => handleChange?.(v)}
                        />
                    )}
                </SkeletonWrapper>
            </Well>

            <Divider size="small" />

            <Well
                className={getClassNames('notification-tracking-builder', levelClassName, className)}
                title="Tracking"
                mode="ghost"
                hideFooter={true}
            >
                <SkeletonWrapper loading={loading}>
                    <TemplateKeywordsField
                        disabled={disabled}
                        value={workingModel.getKeywords()}
                        onChange={(v) => {
                            workingModel.setKeywords(v)
                            handleChange(workingModel)
                        }}
                        options={props.keywordOptions}
                        max={30}
                        tagRender={keywordTagRender}
                        label={
                            <span className="keywords-label">
                                <span className="label-with-component-r">Keywords</span>
                                <Popover
                                    classNames={{
                                        root: getClassNames('popover'),
                                        content: getClassNames('notification-builder-popover', 'keywords-popover'),
                                    }}
                                    content={
                                        <div>
                                            <Typography.Paragraph>
                                                Every user who <strong>clicks</strong> on this notification will be
                                                automatically tagged with the provided keywords. You can then build user
                                                segments based on users tagged with specific keywords.
                                            </Typography.Paragraph>
                                            <Typography.Paragraph>
                                                You can add multiple keywords by pressing the enter/return key or
                                                entering a comma after each individual keyword.
                                            </Typography.Paragraph>
                                        </div>
                                    }
                                >
                                    <InfoCircleOutlined className="info-icon" />
                                </Popover>
                            </span>
                        }
                        placeholder={'Notification keywords'}
                        emptyOptionsPlaceholder={'No previously used keywords'}
                        formItemProps={{ layout: 'vertical' }}
                    />
                </SkeletonWrapper>
            </Well>

            <Divider size="small" />

            <NotificationExperienceOptions
                key="variant-experience-options"
                loading={loading}
                title="Behavior"
                domain={domain}
                disabled={disabled || experienceDisabled}
                hideTtlSettings={hideTtlSettings}
                hideCollapseKeySettings={
                    builder.type === 'template' || !domain.flags?.includes(FEAT_ENABLE_NOTIFICATION_DISPLAY_BEHAVIOR)
                }
                value={workingModel.getExperienceOptions()}
                onChange={(v) => {
                    const update = workingModel.clone()
                    update.setExperienceOptions(v)
                    handleChange(update)
                }}
            />

            {nativeChannelSelected && (
                <>
                    <Divider size="small" />

                    <NotificationChannelSettings
                        key="notification-channel-settings"
                        builder={builder}
                        loading={loading}
                        title="Channel Settings"
                        domain={domain}
                        disabled={disabled}
                        iosValue={value.clone()?.getNativeIosContent()}
                        androidValue={value.clone()?.getNativeAndroidContent()}
                        dispatchChanges={props.dispatchChanges}
                        onChange={(v) => {
                            if (v instanceof NotificationNativeAndroidContentModel) {
                                handleChange(v, DeliveryChannel.NATIVE_ANDROID)
                            }

                            if (v instanceof NotificationNativeIosContentModel) {
                                handleChange(v, DeliveryChannel.NATIVE_IOS)
                            }
                        }}
                        hideNativeIos={!channels.includes(DeliveryChannel.NATIVE_IOS)}
                        hideNativeAndroid={!channels.includes(DeliveryChannel.NATIVE_ANDROID)}
                        hideEnableToggle={true}
                    />
                </>
            )}

            <FieldSuggestionResultsManager
                domain={pageAnalysisDomain}
                results={localFieldSuggestionResults}
                builder={builder}
                dispatch={dispatchChanges}
                onAccept={async (
                    accepted,
                    addtVariants,
                    replaceTargetIndex,
                    additionalReplacements,
                    usedVariantIndices,
                    firstVariantIndex,
                    addVariantIndices,
                ) => {
                    // Use the source from the results that were passed to the drawer
                    // so page_analyzer results are correctly attributed
                    const resultsSource = localFieldSuggestionResults?.source ?? 'page_analyzer'

                    // Resolve the landing URL for page_analyzer source metadata
                    const analyzedUrl =
                        resultsSource === 'page_analyzer'
                            ? (value.getDefaultContent().getLandingUrl() ?? undefined)
                            : undefined

                    // Synchronize auto-fill with advanced drawer selections
                    if (usedVariantIndices?.length) {
                        builder.pushlyAi.markVariantsUsed(usedVariantIndices)
                    }

                    if (accepted) {
                        if (replaceTargetIndex !== undefined && replaceTargetIndex !== builder.selectedVariantIdx) {
                            // Target differs from currently selected variant — apply directly to target
                            const targetVariant = builder.variants[replaceTargetIndex]
                            if (targetVariant) {
                                const update = targetVariant.clone()
                                const contentModel = update.getContent()
                                const defaultContent = contentModel.getDefaultContent().clone()

                                // Apply AI selections to the target's content
                                if (accepted.title) {
                                    defaultContent.setTitle(accepted.title)
                                }
                                if (accepted.body) {
                                    defaultContent.setBody(accepted.body)
                                }
                                if (accepted.imageUrl) {
                                    defaultContent.setImageUrl(accepted.imageUrl)
                                }
                                if (accepted.keywords) {
                                    const existingKw = defaultContent.getKeywords() ?? []
                                    const compiledKws = Array.from(new Set([...existingKw, ...accepted.keywords]))
                                    defaultContent.setKeywords(compiledKws)
                                }

                                // Persist AI generation metadata
                                const tone = accepted.tone ?? accepted.sourceMetadata?.tone
                                const reason = accepted.reason ?? accepted.sourceMetadata?.reason

                                // Build enriched sourceMetadata based on source type
                                let acceptedSourceMetadata: Record<string, any> | null = null
                                if (resultsSource === 'pushly_ai') {
                                    acceptedSourceMetadata = {
                                        ...(builder.pushlyAi.generationIdRef.current
                                            ? { request_id: builder.pushlyAi.generationIdRef.current }
                                            : {}),
                                        ...(firstVariantIndex !== undefined
                                            ? { variant_index: firstVariantIndex }
                                            : {}),
                                        ...(tone ? { tone } : {}),
                                        ...(reason ? { reason } : {}),
                                    }
                                    if (Object.keys(acceptedSourceMetadata).length === 0) acceptedSourceMetadata = null
                                } else if (resultsSource === 'page_analyzer') {
                                    acceptedSourceMetadata = analyzedUrl ? { url: analyzedUrl } : null
                                }

                                defaultContent.setAutoSuggestedFieldsResults({
                                    source: resultsSource,
                                    sourceMetadata: acceptedSourceMetadata,
                                    title: accepted.title ?? null,
                                    body: accepted.body ?? null,
                                    imageUrl: accepted.imageUrl ?? null,
                                    compositeImage: accepted.compositeImage ?? null,
                                    keywords: accepted.keywords ?? null,
                                })

                                contentModel.setDefaultContent(defaultContent)
                                update.setContent(contentModel)

                                dispatchChanges({
                                    entity: 'variant',
                                    type: 'patch',
                                    data: { id: replaceTargetIndex, variant: update },
                                })

                                setLocalFieldSuggestionResults(null)
                            }
                        } else {
                            // Target is the currently selected variant — use existing path
                            applyFieldSuggestionResults(accepted, {
                                source: resultsSource,
                                generationId:
                                    resultsSource === 'pushly_ai'
                                        ? builder.pushlyAi.generationIdRef.current
                                        : undefined,
                                variantIndex: resultsSource === 'pushly_ai' ? firstVariantIndex : undefined,
                                url: resultsSource === 'page_analyzer' ? analyzedUrl : undefined,
                            })
                        }
                    }

                    // Process additional replacements (multi-replace support)
                    if (additionalReplacements?.length) {
                        for (const replacement of additionalReplacements) {
                            const targetVariant = builder.variants[replacement.targetIndex]
                            if (!targetVariant) continue

                            if (replacement.targetIndex === builder.selectedVariantIdx) {
                                // Target is the currently selected variant
                                applyFieldSuggestionResults(replacement.selections, {
                                    source: resultsSource,
                                    generationId:
                                        resultsSource === 'pushly_ai'
                                            ? builder.pushlyAi.generationIdRef.current
                                            : undefined,
                                    variantIndex: resultsSource === 'pushly_ai' ? replacement.variantIndex : undefined,
                                    url: resultsSource === 'page_analyzer' ? analyzedUrl : undefined,
                                })
                            } else {
                                const update = targetVariant.clone()
                                const contentModel = update.getContent()
                                const defaultContent = contentModel.getDefaultContent().clone()

                                if (replacement.selections.title) {
                                    defaultContent.setTitle(replacement.selections.title)
                                }
                                if (replacement.selections.body) {
                                    defaultContent.setBody(replacement.selections.body)
                                }
                                if (replacement.selections.imageUrl) {
                                    defaultContent.setImageUrl(replacement.selections.imageUrl)
                                }
                                if (replacement.selections.keywords) {
                                    const existingKw = defaultContent.getKeywords() ?? []
                                    const compiledKws = Array.from(
                                        new Set([...existingKw, ...replacement.selections.keywords]),
                                    )
                                    defaultContent.setKeywords(compiledKws)
                                }

                                const tone = replacement.selections.tone ?? replacement.selections.sourceMetadata?.tone
                                const reason =
                                    replacement.selections.reason ?? replacement.selections.sourceMetadata?.reason

                                // Build enriched sourceMetadata based on source type
                                let replacementSourceMetadata: Record<string, any> | null = null
                                if (resultsSource === 'pushly_ai') {
                                    replacementSourceMetadata = {
                                        ...(builder.pushlyAi.generationIdRef.current
                                            ? { request_id: builder.pushlyAi.generationIdRef.current }
                                            : {}),
                                        ...(replacement.variantIndex !== undefined
                                            ? { variant_index: replacement.variantIndex }
                                            : {}),
                                        ...(tone ? { tone } : {}),
                                        ...(reason ? { reason } : {}),
                                    }
                                    if (Object.keys(replacementSourceMetadata).length === 0)
                                        replacementSourceMetadata = null
                                } else if (resultsSource === 'page_analyzer') {
                                    replacementSourceMetadata = analyzedUrl ? { url: analyzedUrl } : null
                                }

                                defaultContent.setAutoSuggestedFieldsResults({
                                    source: resultsSource,
                                    sourceMetadata: replacementSourceMetadata,
                                    title: replacement.selections.title ?? null,
                                    body: replacement.selections.body ?? null,
                                    imageUrl: replacement.selections.imageUrl ?? null,
                                    compositeImage: replacement.selections.compositeImage ?? null,
                                    keywords: replacement.selections.keywords ?? null,
                                })

                                contentModel.setDefaultContent(defaultContent)
                                update.setContent(contentModel)

                                dispatchChanges({
                                    entity: 'variant',
                                    type: 'patch',
                                    data: { id: replacement.targetIndex, variant: update },
                                })
                            }
                        }

                        setLocalFieldSuggestionResults(null)
                    }

                    if (addtVariants?.length) {
                        const currentVariant = builder.variants[builder.selectedVariantIdx]!

                        // Check if we can add all variants
                        const totalAfterAdd = builder.variants.length + addtVariants.length
                        if (totalAfterAdd > 4) {
                            simpleNotification(
                                'warning',
                                `Cannot add ${addtVariants.length} variants. Maximum of 4 variants allowed (currently have ${builder.variants.length}).`,
                            )
                            return
                        }

                        // Create all variants without validation or switching
                        const newVariants: NotificationVariantModel[] = []
                        for (let i = 0; i < addtVariants.length; i++) {
                            const variant = addtVariants[i]
                            const nextVariant = currentVariant.clone()

                            // Apply the AI-generated content to the new variant
                            const updatedContent = applyFieldSuggestionResults(variant, {
                                source: resultsSource,
                                skipOnChangeHandler: true,
                                replaceKeywords: true,
                                generationId:
                                    resultsSource === 'pushly_ai'
                                        ? builder.pushlyAi.generationIdRef.current
                                        : undefined,
                                variantIndex: resultsSource === 'pushly_ai' ? addVariantIndices?.[i] : undefined,
                                url: resultsSource === 'page_analyzer' ? analyzedUrl : undefined,
                            })
                            if (updatedContent) {
                                nextVariant.setContent(updatedContent)
                            }

                            newVariants.push(nextVariant)
                        }

                        // Add all variants at once using the new batch action
                        dispatchChanges({
                            entity: 'variants',
                            type: 'add-multi',
                            data: { variants: newVariants },
                        })

                        // Only show the success toast if we're already in A/B mode.
                        // When transitioning into A/B mode (1→N), the "A/B Testing enabled"
                        // notification in NotificationBuilder handles the feedback.
                        if (builder.variants.length > 1) {
                            simpleNotification('success', `${addtVariants.length} variants added successfully!`)
                        }

                        // Clear composite image state to prevent ghosting on the currently viewed variant
                        setSuggestedImageComposite(undefined)
                    }
                }}
                onClose={() => {
                    setLocalFieldSuggestionResults(null)
                }}
            />
        </>
    )
}

export default NotificationContentBuilder
